/**
 * COMMERCIAL LAYER epic (ADR-075 Part C). The ONE place apps/portal talks to
 * the backend - mirrors apps/ops/src/api/client.ts's shape exactly (same
 * ErrorResponseBody contract: {code, params, request_id}), but a genuinely
 * separate storage key and a separate session token space: a seller
 * session token is never the same bearer this app's sibling (apps/ops)
 * would send, and this file has no knowledge of office auth at all.
 */

const STORAGE_KEY = 'wms_portal_session_token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(STORAGE_KEY, token);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage may be unavailable (private mode) - the session still works
    // for the lifetime of this tab via the in-memory AuthContext state.
  }
}

export type ApiErrorBody = {
  readonly code: string;
  readonly params: Readonly<Record<string, unknown>>;
  readonly request_id: string;
};

export class ApiError extends Error {
  readonly code: string;
  readonly params: Readonly<Record<string, unknown>>;
  readonly requestId: string;
  readonly status: number;

  constructor(status: number, body: ApiErrorBody) {
    super(`${body.code} (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.params = body.params;
    this.requestId = body.request_id;
  }
}

/** Thrown when the browser cannot reach the API at all (offline, DNS, CORS misconfig). */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('The seller portal could not be reached. Check your connection and try again.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export type Query = Record<string, string | number | boolean | null | undefined>;

function buildQuery(query?: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined || v === '') continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

async function request<T>(method: 'GET' | 'POST' | 'PUT', path: string, opts: { query?: Query; body?: unknown } = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${buildQuery(opts.query)}`, {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (err) {
    throw new NetworkError(err);
  }

  if (res.status === 204) return undefined as T;

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    json = null;
  }

  if (!res.ok) {
    const body = (json as ApiErrorBody | null) ?? { code: 'INTERNAL.UNEXPECTED', params: {}, request_id: 'unknown' };
    throw new ApiError(res.status, body);
  }
  return json as T;
}

export const api = {
  get: <T>(path: string, query?: Query): Promise<T> => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, { body }),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>('PUT', path, { body }),
};
